import type { Component } from 'vue'
import CloGainOutput from '~/components/assets/configurations/CloGainOutput.vue'
import { flattenFields, type LeafFieldConfig } from '~/components/common/form/form.utils'
import type { FieldConfig } from '~/components/common/form/form.types'
import type { AssetCapabilities, AssetCapability, AssetCapabilityKind } from '~/types/capabilities'
import type {
  AssetConfiguration,
  AssetConfigurationSync,
  AssetConfigurationSyncEntry,
  ConfigurationCardMode,
  ConfigurationFieldDrift,
  ConfigurationValue,
  ConfigurationValues,
  ParameterDriftStatus
} from '~/types/asset-configuration'

// TODO: remove once the capabilities registry ships an explicit `kind` on every action.
export const CONFIGURATION_ACTION_KEYS = [
  'setInterval',
  'setAlertSettings',
  'setAlsSettings',
  'setDstConfig',
  'setDim'
] as const

type ConfigurationActionKey = (typeof CONFIGURATION_ACTION_KEYS)[number]

const isConfigurationActionKey = (actionKey: string): actionKey is ConfigurationActionKey =>
  (CONFIGURATION_ACTION_KEYS as readonly string[]).includes(actionKey)

const READ_ACTION_KEY_PATTERN = /^(get|fetch|read)/i

export const resolveActionKind = (capability: AssetCapability): AssetCapabilityKind => {
  if (capability.kind) return capability.kind
  if (isConfigurationActionKey(capability.actionKey)) return 'configuration'
  return READ_ACTION_KEY_PATTERN.test(capability.actionKey) ? 'read' : 'command'
}

export const isConfigurationCapability = (capability: AssetCapability): boolean =>
  resolveActionKind(capability) === 'configuration'

// A vendor configuration keeps its values on the asset: they are not read from the device states
export const isVendorCapability = (capability: Pick<AssetCapability, 'source'>): boolean =>
  capability.source === 'vendors'

// What a configuration currently does on the device, shown under its fields.
// Each component receives the asset last `states` and the configuration `values`.
export const CONFIGURATION_RESULT_COMPONENTS: Record<string, Component> = {
  setCloGainProfile: CloGainOutput
}

export const resolveConfigurationResultComponent = (capability: AssetCapability): Component | null =>
  CONFIGURATION_RESULT_COMPONENTS[capability.actionKey] ?? null

export const resolveReadActionKey = (capability: AssetCapability, available: AssetCapabilities): string | null => {
  const readActionKey = capability.readActionKey
  if (!readActionKey) return null
  return available.some((item) => item.actionKey === readActionKey) ? readActionKey : null
}

export interface SplitCapabilitiesByKind {
  configurations: AssetCapability[]
  actions: AssetCapability[]
}

export const splitCapabilitiesByKind = (capabilities: AssetCapabilities): SplitCapabilitiesByKind => {
  const configurations: AssetCapability[] = []
  const actions: AssetCapability[] = []
  // A read paired with a configuration is invoked from its card, so it stays out of the actions list
  const pairedReadKeys = new Set(
    capabilities
      .filter(isConfigurationCapability)
      .map((capability) => capability.readActionKey)
      .filter((readActionKey): readActionKey is string => !!readActionKey)
  )
  for (const capability of capabilities) {
    if (isConfigurationCapability(capability)) {
      configurations.push(capability)
    } else if (!pairedReadKeys.has(capability.actionKey)) {
      actions.push(capability)
    }
  }
  return { configurations, actions }
}

export const parameterNameKeys = (capability: AssetCapability): string[] =>
  (capability.capabilities ?? []).flatMap((entry) => (entry.parameters ?? []).map((parameter) => parameter.nameKey))

const normalizeValue = (value: ConfigurationValue): string | null =>
  value === null || value === '' ? null : String(value)

const resolveDriftStatus = (deviceValue: ConfigurationValue, presetValue: ConfigurationValue): ParameterDriftStatus =>
  normalizeValue(deviceValue) === normalizeValue(presetValue) ? 'inSync' : 'outOfSync'

export const computeConfigurationDrift = (
  fieldKeys: string[],
  deviceValues: ConfigurationValues,
  presetValues: ConfigurationValues
): ConfigurationFieldDrift[] =>
  fieldKeys
    .filter((nameKey) => nameKey in presetValues)
    .map((nameKey) => {
      const deviceValue = deviceValues[nameKey] ?? null
      const presetValue = presetValues[nameKey] ?? null
      return { nameKey, deviceValue, presetValue, status: resolveDriftStatus(deviceValue, presetValue) }
    })

export interface ResolveCardModeInput {
  hasPreset: boolean
  drifts: ConfigurationFieldDrift[]
  sentAt: string | null
  readAt: string | null
}

const isAfter = (isoDate: string, otherIsoDate: string | null): boolean =>
  otherIsoDate === null || new Date(isoDate).getTime() > new Date(otherIsoDate).getTime()

export const isAwaitingDevice = (sentAt: string | null, readAt: string | null): boolean =>
  sentAt !== null && isAfter(sentAt, readAt)

export const resolveCardMode = ({ hasPreset, drifts, sentAt, readAt }: ResolveCardModeInput): ConfigurationCardMode => {
  if (!hasPreset) return 'device'
  if (isAwaitingDevice(sentAt, readAt)) return 'inProgress'
  return drifts.every((drift) => drift.status === 'inSync') ? 'synced' : 'outOfSync'
}

export interface PendingPresetSync {
  baselineStatusDate: string | null
}

export const resolveCardModeFromSync = (
  entry: AssetConfigurationSyncEntry | null,
  pendingSync: PendingPresetSync | null
): Exclude<ConfigurationCardMode, 'device'> => {
  if (pendingSync && (entry?.statusDate ?? null) === pendingSync.baselineStatusDate) return 'inProgress'
  return entry?.state ?? 'unknown'
}

export interface ConfigurationCardState {
  drifts: ConfigurationFieldDrift[]
  mode: ConfigurationCardMode
}

export const resolveConfigurationCardState = (
  capability: AssetCapability,
  configuration: AssetConfiguration | null,
  sentAt: string | null,
  pendingSync: PendingPresetSync | null = null
): ConfigurationCardState => {
  const preset = configuration?.preset ?? null
  const drifts = computeConfigurationDrift(
    parameterNameKeys(capability),
    configuration?.values ?? {},
    preset?.values ?? {}
  )
  const mode =
    preset && configuration?.sync !== undefined
      ? resolveCardModeFromSync(configuration.sync, pendingSync)
      : resolveCardMode({ hasPreset: !!preset, drifts, sentAt, readAt: configuration?.readAt ?? null })
  if (mode !== 'synced') return { drifts, mode }
  const syncedAt = configuration?.sync?.statusDate ?? null
  const readAtByKey = configuration?.readAtByKey ?? {}
  return {
    drifts: drifts.map((drift) =>
      drift.status === 'outOfSync' && isReadAfterSync(readAtByKey[drift.nameKey] ?? null, syncedAt)
        ? { ...drift, status: 'adjusted' }
        : drift
    ),
    mode
  }
}

const isReadAfterSync = (readAt: string | null, syncedAt: string | null): boolean =>
  readAt !== null && syncedAt !== null && new Date(readAt).getTime() >= new Date(syncedAt).getTime()

export const assetSyncTargetCount = (sync: AssetConfigurationSync | null | undefined): number => {
  if (!sync || (sync.state !== 'outOfSync' && sync.state !== 'unknown')) return 0
  return sync.entries.filter((entry) => entry.state === 'outOfSync' || entry.state === 'unknown').length
}

export type ConfigurationCardRow =
  { kind: 'divider'; id: string; label: string } | { kind: 'field'; id: string; field: LeafFieldConfig }

export const buildFieldRows = (
  fields: FieldConfig[],
  matches: (text: string | undefined) => boolean = () => true,
  titleMatches = true
): ConfigurationCardRow[] =>
  fields.flatMap((field): ConfigurationCardRow[] => {
    if (field.type !== 'group') {
      return titleMatches || matches(field.label) ? [{ kind: 'field', id: field.id, field }] : []
    }
    const children = flattenFields(field.children)
    const visibleChildren =
      titleMatches || matches(field.label) ? children : children.filter((child) => matches(child.label))
    if (!visibleChildren.length) return []
    return [
      { kind: 'divider', id: field.id, label: field.label ?? '' },
      ...visibleChildren.map((child): ConfigurationCardRow => ({ kind: 'field', id: child.id, field: child }))
    ]
  })

export const buildConfigurationPayload = (
  baseline: ConfigurationValues,
  edited: ConfigurationValues
): ConfigurationValues => ({
  ...baseline,
  ...edited
})
